import { PanelType, PivotItem } from "@fluentui/react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { IResult, ISelectedKey } from "../../SharedComponents/Interfaces/CustomContact.interfaces";
import CustomPanel from "../../SharedComponents/CustomPanel/CustomPanel";
import { TabsContainer } from "../../SharedComponents/TabsContainer";
import {
  useGetCustomerContactListQuery,
  useGetCustomerCxEContactListQuery,
  useGetCustomerManualContactListQuery,
  useGetCSAMContactListQuery,
  useGetEngagementContactsQuery
} from "../../slices/ExploreCustomers.slice";
import { CustomContactList } from "../../SharedComponents/CustomContactList";
import { GET_API_NAMES } from "../../SharedComponents/Constants/CustomContactList.constants";
import { EngagementContactsSection } from "./EngagementContactsSection";
import ContactListShimmer from "./ContactListShimmer";

// Helper to normalize hook results
const normalizeResult = ({ data, error, isLoading, isSuccess }: any) => ({
  data: data || [],
  error,
  isLoading,
  isSuccess
});

const ContactsPanel = ({
  isOpen,
  onDismiss,
  tenantName,
  tpid,
  tenantId: propTenantId,
  accountManager,
  accountManagerAlias
}: {
  isOpen: boolean;
  onDismiss: () => void;
  tenantName: string;
  tpid?: string;
  tenantId?: string;
  accountManager?: string;
  accountManagerAlias?: string;
}) => {
  const [selectedKey, setSelectedKey] = useState<ISelectedKey["selectedKey"]>("cxeContacts");
  const [params] = useSearchParams();
  const tenantId = propTenantId || params.get("id");

  // Defer all contact fetches until the panel is actually open. This avoids firing
  // getCustomerCxEContactList (and siblings) on initial customer-page load, freeing
  // up browser connections for the visible tab (e.g. Product Telemetry).
  const automatedContacts = useGetCustomerContactListQuery(tenantId || "", {
    skip: !isOpen || selectedKey !== "automatedContacts"
  });
  const cxeContacts = useGetCustomerCxEContactListQuery(tenantId || "", {
    skip: !isOpen || selectedKey !== "cxeContacts"
  });
  const manualContacts = useGetCustomerManualContactListQuery(tenantId || "", {
    skip: !isOpen || selectedKey !== "manualContacts"
  });
  const csamContacts = useGetCSAMContactListQuery(tpid || "", {
    skip: !isOpen || selectedKey !== "csamContacts" || !tpid
  });
  const engagementContacts = useGetEngagementContactsQuery(tenantId || "", {
    skip: !isOpen || selectedKey !== "csamContacts" || !tenantId
  });

  // For Account Contacts tab, check if ALL data is loaded before rendering
  const isAccountContactsLoading = selectedKey === "csamContacts" &&
    (csamContacts.isLoading || engagementContacts.isLoading);

  const dataMapping: Partial<{ [key in ISelectedKey["selectedKey"]]: IResult }> = {
    automatedContacts: normalizeResult(automatedContacts),
    cxeContacts: normalizeResult(cxeContacts),
    manualContacts: normalizeResult(manualContacts),
    csamContacts: normalizeResult(csamContacts)
  };
  const selectedData = dataMapping[selectedKey];

  useEffect(() => {
    if (selectedData?.data && selectedData?.isSuccess) {
      logEvent(`${GET_API_NAMES[selectedKey]} API success`, "Customer Page", "ContactsPanel");
    }
    if (selectedData?.error) {
      logException("Customer Page", "ContactsPanel", GET_API_NAMES[selectedKey], `${selectedKey}Error`);
    }
  }, [selectedKey, selectedData]);

  const onLinkClick = (item: any) => {
    setSelectedKey(item.props.itemKey);
  };

  // Render unified loading shimmer for Account Contacts tab
  const renderAccountContactsLoading = () => (
    <>
      <ContactListShimmer />
      <ContactListShimmer />
      <ContactListShimmer />
      <ContactListShimmer />
      <br />
    </>
  );

  return (
    <CustomPanel
      enablePanel={isOpen}
      dismissPanel={onDismiss}
      panelTitle={`Contacts for ${tenantName ?? "Customer"}`}
      panelType={PanelType.custom}
      isLoading={false}
      customWidth="600px"
    >
      <TabsContainer selectedKey={selectedKey} ariaLabel="Contacts tabs" handleSelectedTab={onLinkClick}>
        <PivotItem headerText="CxE Contacts" itemKey="cxeContacts" />
        <PivotItem headerText="Account Contacts" itemKey="csamContacts" />
        <PivotItem headerText="Manual Contacts" itemKey="manualContacts" />
        <PivotItem headerText="Automated Contacts" itemKey="automatedContacts" />
      </TabsContainer>
      <div style={{ marginTop: "20px" }}>
        {/* Account Contacts tab - show unified loader until ALL data is ready */}
        {selectedKey === "csamContacts" ? (
          isAccountContactsLoading ? (
            renderAccountContactsLoading()
          ) : (
            <>
              <EngagementContactsSection
                contactData={normalizeResult(engagementContacts)}
                accountManager={accountManager}
                accountManagerAlias={accountManagerAlias}
              />
              <CustomContactList
                selectedKey={selectedKey}
                contactData={{ ...normalizeResult(csamContacts), isLoading: false }}
              />
            </>
          )
        ) : (
          <CustomContactList
            selectedKey={selectedKey}
            contactData={selectedData ?? { data: [], error: undefined, isLoading: false, isSuccess: false }}
          />
        )}
      </div>
    </CustomPanel>
  );
};

export default ContactsPanel;
